import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { LEVELS, findLevel, getInitialState, applyRules, isLevelSolved } from "@/data/levels";
import { RELICS } from "@/data/relics";
import { GRIMOIRE_PAGES } from "@/data/grimoire";
import { usePlayer } from "@/context/PlayerContext";
import EraPanel from "@/components/EraPanel";
import GhostHint from "@/components/GhostHint";
import PedagogicalCard from "@/components/PedagogicalCard";
import { Button } from "@/components/ui/button";
import { ChevronLeft, RotateCcw, HelpCircle, Sparkles, Timer, GraduationCap, Lock, Trophy, Share2 } from "lucide-react";
import { getPedagogicalCard } from "@/data/pedagogical";
import { toast } from "sonner";
import { readTodayUsageSeconds, addTodayUsageSeconds } from "@/lib/storage";
import { api } from "@/lib/api";
import { playClick, playPlace, playError, playWin, playHint, playAmbient, stopAmbient } from "@/lib/sound";
import { share, reportShareToast } from "@/lib/share";
import { localizeLevel, localizeRelic, localizeGrimoirePage } from "@/i18n/game";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SoundToggle from "@/components/SoundToggle";

export default function Play() {
  const { t } = useTranslation();
  const { levelId } = useParams();
  const [searchParams] = useSearchParams();
  const isDaily = searchParams.get("daily") === "1";
  const navigate = useNavigate();
  const { player, commitProgress, loading: playerLoading } = usePlayer();

  const rawLevel = useMemo(() => findLevel(levelId), [levelId]);
  const level = useMemo(() => (rawLevel ? localizeLevel(rawLevel, t) : null), [rawLevel, t]);
  const isEveil = player?.mode === "eveil" || rawLevel?.eveil;

  const [state, setState] = useState(() => (level ? getInitialState(level) : {}));
  const [focusedEra, setFocusedEra] = useState("medieval");
  const [dragging, setDragging] = useState(null);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [hintOpen, setHintOpen] = useState(false);
  const [solved, setSolved] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [flashTimeLeft, setFlashTimeLeft] = useState(60);
  const [flashFailed, setFlashFailed] = useState(false);
  const startedAtRef = useRef(Date.now());
  const timerRef = useRef(null);
  const solvedRef = useRef(false);
  const isFlash = searchParams.get("flash") === "1";

  const [placedObjs, setPlacedObjs] = useState({});
  const [showPedagogical, setShowPedagogical] = useState(false);
  const [screenTimeBlocked, setScreenTimeBlocked] = useState(false);
  const [usageSeconds, setUsageSeconds] = useState(0);

  useEffect(() => {
    if (showWin || screenTimeBlocked || flashFailed) { stopAmbient(); return; }
    if (focusedEra) playAmbient(focusedEra);
    return () => stopAmbient();
  }, [focusedEra, showWin, screenTimeBlocked, flashFailed]);

  useEffect(() => () => stopAmbient(), []);

  useEffect(() => {
    if (!player) return;
    const check = () => {
      const used = readTodayUsageSeconds(player.id);
      setUsageSeconds(used);
      if (player.screen_time_enabled && player.screen_time_daily_minutes > 0) {
        if (used >= player.screen_time_daily_minutes * 60) {
          setScreenTimeBlocked(true);
        }
      }
    };
    check();
    const iv = setInterval(check, 15000);
    return () => clearInterval(iv);
  }, [player]);

  useEffect(() => {
    if (!player || showWin || screenTimeBlocked) return;
    const iv = setInterval(() => addTodayUsageSeconds(player.id, 5), 5000);
    return () => clearInterval(iv);
  }, [player, showWin, screenTimeBlocked]);

  useEffect(() => {
    if (playerLoading) return;
    if (!player) { navigate("/profile"); return; }
    if (!rawLevel) { navigate("/levels"); return; }
  }, [playerLoading, player, rawLevel, navigate]);

  useEffect(() => {
    if (!level) return;
    setState(getInitialState(level));
    setSolved(false);
    solvedRef.current = false;
    setShowWin(false);
    setPlacedObjs({});
    setSelectedPiece(null);
    startedAtRef.current = Date.now();
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    // Éveil: auto-open Luna's hint so young kids hear the instruction
    if (isEveil) {
      const t = setTimeout(() => setHintOpen(true), 600);
      return () => { clearInterval(timerRef.current); clearTimeout(t); };
    }
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId, isEveil]);

  useEffect(() => {
    if (!isFlash || showWin || flashFailed) return;
    const iv = setInterval(() => {
      const left = Math.max(0, 60 - Math.floor((Date.now() - startedAtRef.current) / 1000));
      setFlashTimeLeft(left);
      if (left <= 0 && !solvedRef.current) {
        setFlashFailed(true);
        clearInterval(iv);
        if (player && level) {
          api.post("/flash-challenge/result", {
            player_id: player.id, level_id: level.id, seconds: 60, solved: false,
          }).catch(() => {});
        }
      }
    }, 500);
    return () => clearInterval(iv);
  }, [isFlash, showWin, flashFailed, player, level]);

  useEffect(() => {
    if (!solved || !level || !player) return;
    if (solvedRef.current) return;
    solvedRef.current = true;
    clearInterval(timerRef.current);
    playWin();
    const seconds = Math.floor((Date.now() - startedAtRef.current) / 1000);
    setShowWin(true);

    if (isFlash) {
      const solvedInTime = seconds <= 60;
      api.post("/flash-challenge/result", {
        player_id: player.id, level_id: level.id, seconds, solved: true,
      }).then((r) => {
        if (r.data.shards_bonus > 0) toast.success(t("play.flash_bonus", { n: r.data.shards_bonus }));
        else if (r.data.already_rewarded) toast(t("play.flash_already"));
      }).catch(() => {});
      commitProgress({
        level_id: 0, memory_shards_gained: 0, playtime_seconds_added: seconds,
        hints_used_added: 0, mode: player.mode,
      });
    } else if (isDaily) {
      commitProgress({
        level_id: 0,
        memory_shards_gained: 5,
        playtime_seconds_added: seconds,
        hints_used_added: 0,
        mode: player.mode,
      });
      api.post("/daily-challenge/score", {
        player_id: player.id, level_id: level.id, seconds,
      }).then((r) => {
        if (!r.data.kept) toast.success(t("play.daily_record", { s: seconds }));
      }).catch(() => {});
    } else {
      commitProgress({
        level_id: level.id,
        memory_shards_gained: level.reward.shards,
        relic_unlocked: level.reward.relic,
        grimoire_page_unlocked: level.reward.grimoire_page,
        playtime_seconds_added: seconds,
        hints_used_added: 0,
        mode: player.mode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solved]);

  if (playerLoading || !level || !player) return null;

  const handleDropObject = (eraKey, slotId, obj) => {
    const era = level.eras[eraKey];
    const slot = era.slots.find((s) => s.id === slotId);
    if (!slot?.accepts?.includes(obj.id)) {
      playError();
      toast.error(t("play.piece_wrong"));
      return;
    }
    playPlace();
    setPlacedObjs((prev) => {
      const setForEra = new Set(prev[eraKey] || []);
      setForEra.add(obj.id);
      return { ...prev, [eraKey]: setForEra };
    });
    setState((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next[eraKey].slots[slotId].contents = obj.id;
      const withRules = applyRules(level, next);
      if (isLevelSolved(level, withRules) && !solvedRef.current) {
        setTimeout(() => setSolved(true), 0);
      }
      return withRules;
    });
    setSelectedPiece(null);
    toast.success(t("play.piece_placed", { name: obj.label }));
  };

  const handleReset = () => {
    setState(getInitialState(level));
    setPlacedObjs({});
    setSolved(false);
    solvedRef.current = false;
    setSelectedPiece(null);
    startedAtRef.current = Date.now();
    setElapsed(0);
  };

  const handleHint = () => {
    playHint();
    setHintOpen(true);
    commitProgress({ level_id: 0, hints_used_added: 1, playtime_seconds_added: 0, memory_shards_gained: 0 });
  };

  const orderedEras = ["antiquity", "medieval", "renaissance", "future"].filter((k) => level.eras[k]);

  const availableObjectsByEra = (eraKey) => {
    const placed = placedObjs[eraKey] || new Set();
    return (level.eras[eraKey].objects || []).filter((o) => !placed.has(o.id));
  };

  const rawGrimoire = GRIMOIRE_PAGES.find((g) => g.id === level.reward.grimoire_page);
  const grimoirePage = rawGrimoire ? localizeGrimoirePage(rawGrimoire, t) : null;
  const relic = RELICS[level.reward.relic] ? localizeRelic(level.reward.relic, RELICS[level.reward.relic], t) : null;

  return (
    <div className="relative min-h-screen bg-stone-950 grain" data-eveil={isEveil ? "1" : "0"}>
      <header className="relative z-20 flex items-center justify-between px-4 sm:px-8 py-4 glass-strong border-b border-white/10">
        <Link to="/levels" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-levels">
          <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t("play.back_short")}</span>
        </Link>
        <div className="text-center flex-1 px-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold flex items-center justify-center gap-1">
            {isDaily && <Trophy className="w-3 h-3 text-fuchsia-300" />}
            {isDaily ? t("play.header_daily_prefix") : ""}{level.subtitle}
          </div>
          <div className="font-serif text-lg sm:text-xl font-bold text-stone-50">{level.title}</div>
        </div>
        <div className="flex items-center gap-2">
          {player.mode === "engineer" && !isFlash && !isEveil && (
            <div className="glass rounded-full px-3 py-1 text-xs flex items-center gap-1.5 font-mono" data-testid="hud-timer">
              <Timer className="w-3.5 h-3.5 text-teal-300" />
              <span>{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}</span>
            </div>
          )}
          {isFlash && (
            <div className={`rounded-full px-3 py-1 text-xs flex items-center gap-1.5 font-mono font-bold ${flashTimeLeft <= 10 ? "bg-rose-500 text-stone-50" : "glass"}`} data-testid="hud-flash-timer">
              <Timer className="w-3.5 h-3.5" />
              <span>{flashTimeLeft}s</span>
            </div>
          )}
          <button onClick={handleReset} className="glass rounded-full p-2 hover:border-amber-400/50" data-testid="btn-reset" title={t("play.reset_title")}>
            <RotateCcw className="w-4 h-4" />
          </button>
          <button onClick={handleHint} className="glass rounded-full p-2 hover:border-amber-400/50" data-testid="btn-hint" title={t("play.hint_title")}>
            <HelpCircle className="w-4 h-4" />
          </button>
          <SoundToggle />
          <LanguageSwitcher compact />
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6 flex flex-col gap-3 sm:gap-4">
        {orderedEras.map((eraKey) => (
          <EraPanel
            key={eraKey}
            eraKey={eraKey}
            eraDef={level.eras[eraKey]}
            slotStates={state[eraKey].slots}
            focused={focusedEra === eraKey}
            onFocus={() => setFocusedEra(eraKey)}
            onDropObject={handleDropObject}
            dragging={dragging}
            setDragging={setDragging}
            selectedPiece={selectedPiece}
            setSelectedPiece={setSelectedPiece}
            availableObjects={availableObjectsByEra(eraKey)}
          />
        ))}

        <div className="glass rounded-2xl p-4 text-sm text-stone-300">
          <span className="font-bold text-amber-300 uppercase text-xs tracking-widest mr-2">{t("play.objective_label")}</span>
          {t(isEveil ? "play.objective_text_eveil" : "play.objective_text")}
        </div>
      </main>

      <GhostHint
        open={hintOpen}
        message={player.mode === "engineer" ? level.hint_engineer : level.hint_discovery}
        onClose={() => setHintOpen(false)}
        autoSpeak={isEveil}
      />

      <AnimatePresence>
        {showWin && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            data-testid="win-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              className="glass-strong rounded-3xl p-8 max-w-lg w-full text-center"
            >
              <div className="text-6xl mb-3 shard-glow">🌟</div>
              <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">{isDaily ? t("play.win_kicker_daily") : isFlash ? t("play.win_kicker_flash") : t("play.win_kicker")}</div>
              <h3 className="mt-2 font-serif text-3xl sm:text-4xl font-black text-stone-50">{level.title}</h3>

              {isDaily ? (
                <div className="mt-6 flex justify-center">
                  <div className="glass rounded-2xl p-4 min-w-40" data-testid="win-daily-reward">
                    <div className="text-amber-300 font-serif text-3xl font-black">+5</div>
                    <div className="text-xs text-stone-400 mt-1">{t("play.reward_shards")}</div>
                  </div>
                </div>
              ) : isFlash ? (
                <div className="mt-6 text-sm text-stone-300" data-testid="win-flash-note">
                  {t("play.flash_reward_note")}
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-3 gap-3 text-sm">
                  <div className="glass rounded-2xl p-3">
                    <div className="text-amber-300 font-serif text-2xl font-black">+{level.reward.shards}</div>
                    <div className="text-xs text-stone-400 mt-1">{t("play.reward_shards")}</div>
                  </div>
                  <div className="glass rounded-2xl p-3">
                    <div className="text-3xl">{relic?.icon}</div>
                    <div className="text-xs text-stone-400 mt-1">{relic?.name}</div>
                  </div>
                  <div className="glass rounded-2xl p-3">
                    <div className="text-teal-300 font-serif text-2xl font-black">§{level.reward.grimoire_page}</div>
                    <div className="text-xs text-stone-400 mt-1">{t("play.reward_page")}</div>
                  </div>
                </div>
              )}

              {grimoirePage && !isDaily && !isFlash && (
                <div className="mt-6 text-left glass rounded-2xl p-4">
                  <div className="text-xs uppercase tracking-widest text-amber-300/80 font-bold">{t("play.new_page")}</div>
                  <div className="mt-1 font-serif text-xl font-bold text-stone-50">{grimoirePage.title}</div>
                  <p className="text-sm text-stone-300 mt-2 italic">« {grimoirePage.panels[0]} »</p>
                </div>
              )}

              <div className="mt-6 flex gap-3 justify-center flex-wrap">
                {getPedagogicalCard(level.id) && !isDaily && !isFlash && (
                  <Button
                    onClick={() => setShowPedagogical(true)}
                    variant="outline"
                    className="border-emerald-400/50 bg-transparent text-emerald-200 hover:bg-emerald-900/20 rounded-full px-6"
                    data-testid="btn-open-pedagogical"
                  >
                    <GraduationCap className="w-4 h-4 mr-2" />
                    {t("play.pedagogical_btn")}
                  </Button>
                )}
                <Button
                  onClick={async () => {
                    const r = await share({
                      title: `Chronos Time Puzzle — ${level.title}`,
                      text: t("share.win_text", { title: level.title, name: player.name }),
                      url: window.location.origin,
                    });
                    reportShareToast(r, t);
                  }}
                  variant="outline"
                  className="border-fuchsia-400/50 bg-transparent text-fuchsia-200 hover:bg-fuchsia-900/20 rounded-full px-6"
                  data-testid="btn-share-win"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  {t("share.share")}
                </Button>
                <Button
                  onClick={() => navigate("/levels")}
                  variant="outline"
                  className="border-stone-500/50 bg-transparent text-stone-200 hover:bg-stone-800 rounded-full px-6"
                  data-testid="btn-back-levels"
                >{t("play.back_map")}</Button>
                <Button
                  onClick={() => {
                    if (isDaily || isFlash) { navigate("/levels"); return; }
                    const next = findLevel(level.id + 1);
                    if (next) navigate(`/play/${next.id}`);
                    else navigate("/grimoire");
                  }}
                  className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6"
                  data-testid="btn-next-level"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {isDaily ? t("play.back_map") : isFlash ? t("play.back_map") : (findLevel(level.id + 1) ? t("play.next_chapter") : t("play.see_grimoire"))}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {screenTimeBlocked && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur" data-testid="screen-time-gate">
          <div className="glass-strong rounded-3xl p-8 max-w-md w-full text-center">
            <Lock className="w-14 h-14 text-amber-300 mx-auto" />
            <h3 className="mt-3 font-serif text-3xl font-black text-stone-50">{t("play.screen_time_title")}</h3>
            <p className="mt-2 text-stone-300 text-sm">{t("play.screen_time_text", { minutes: player?.screen_time_daily_minutes })}</p>
            <Button
              onClick={() => navigate("/levels")}
              className="mt-6 bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6"
              data-testid="btn-screen-time-ok"
            >{t("common.ok")}</Button>
          </div>
        </div>
      )}

      {flashFailed && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-black/85 backdrop-blur" data-testid="flash-failed">
          <div className="glass-strong rounded-3xl p-8 max-w-md w-full text-center">
            <Timer className="w-14 h-14 text-rose-400 mx-auto" />
            <h3 className="mt-3 font-serif text-3xl font-black text-stone-50">{t("play.flash_title")}</h3>
            <p className="mt-2 text-stone-300 text-sm">{t("play.flash_text")}</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Button onClick={() => window.location.reload()} className="bg-fuchsia-500 hover:bg-fuchsia-400 text-stone-950 font-bold rounded-full px-6" data-testid="btn-flash-retry">
                {t("common.retry")}
              </Button>
              <Button onClick={() => navigate("/levels")} variant="outline" className="border-stone-500/50 bg-transparent text-stone-200 rounded-full px-6">{t("common.back")}</Button>
            </div>
          </div>
        </div>
      )}

      <PedagogicalCard
        open={showPedagogical}
        onClose={() => setShowPedagogical(false)}
        levelId={level.id}
        mode={player.mode}
      />
    </div>
  );
}
